import { useDraggable, useDroppable } from '@dnd-kit/core';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

const messages = defineMessages({
  page: {
    id: 'autoCuration.pdfSplitEditor.pageNumber',
    defaultMessage: 'Page {num}',
  },
  mergeWithPrevGroup: {
    id: 'autoCuration.pdfSplitEditor.mergeWithPrevGroup',
    defaultMessage: 'Merge with previous group',
  },
  mergeWithNextGroup: {
    id: 'autoCuration.pdfSplitEditor.mergeWithNextGroup',
    defaultMessage: 'Merge with next group',
  },
  splitHere: {
    id: 'autoCuration.pdfSplitEditor.splitHere',
    defaultMessage: 'Split here',
  },
});

export interface GroupRowProps {
  group: number[];
  groupIndex: number;
  currentPage: number;
  prevGroup: number;
  nextGroup: number;
  canSplitPrev: boolean;
  canSplitNext: boolean;
  onPageClick: (page: number) => void;
  onSplitPage: (page: number) => void;
}

interface ComponentProps extends GroupRowProps {
  pageNum: number;
  pageIndexInGroup: number;
  onMergePrevious: (page: number) => void;
  onMergeNext: (page: number) => void;
}

export function GroupRow({
  group,
  groupIndex,
  currentPage,
  pageNum,
  pageIndexInGroup,
  canSplitNext,
  canSplitPrev,
  nextGroup,
  prevGroup,
  onSplitPage,
  onPageClick,
  onMergeNext,
  onMergePrevious,
}: ComponentProps) {
  const { formatMessage } = useIntl();
  const isThisPageCurrent = pageNum === currentPage;
  const canSplitNextPage = pageIndexInGroup < group.length - 1;
  const isFirstPage = pageIndexInGroup === 0;
  const isLastPage = pageIndexInGroup === group.length - 1;

  const handlePageClick = () => {
    if (currentPage === pageNum) {
      return;
    }
    onPageClick(pageNum);
  };

  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
  } = useDraggable({
    id: pageNum,
  });

  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: pageNum,
  });

  return (
    <Box
      ref={(node: HTMLElement) => {
        setDragRef(node);
        setDropRef(node);
      }}
      {...listeners}
      {...attributes}
      sx={{
        backgroundColor: isOver ? 'info.light' : undefined,
        cursor: 'grab',
      }}
    >
      <Box
        data-page={pageNum}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          padding: '4px',
          borderRadius: '6px',
          transition: 'all 0.2s',
        }}
      >
        {/* Drag handle - 3x3 grid of dots */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '1px',
            width: '12px',
            height: '12px',
            cursor: 'grab',
            opacity: 1,
            transform: 'scale(1)',
            transition: 'all 0.2s',
            '&:active': {
              cursor: 'grabbing',
              transform: 'scale(1.1)',
            },
            '&:hover': {
              opacity: 0.7,
              transform: 'scale(1.05)',
            },
          }}
        >
          {Array.from({ length: 9 }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: '2px',
                height: '2px',
                bgcolor: 'text.secondary',
                borderRadius: '50%',
              }}
            />
          ))}
        </Box>

        {/* Merge with previous group button */}
        {isFirstPage && canSplitPrev && (
          <IconButton
            onClick={() => onMergePrevious(groupIndex)}
            size="small"
            title={formatMessage(messages.mergeWithPrevGroup)}
            sx={{
              bgcolor: 'white',
              color: (theme) => theme.palette.success.main,
              border: '1.5px solid',
              borderColor: (theme) => theme.palette.success.main,
              borderRadius: '6px',
              minWidth: '36px',
              height: '28px',
              fontSize: '0.75rem',
              padding: '4px 8px',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: (theme) => theme.palette.success.main,
                borderColor: (theme) => theme.palette.success.main,
                color: 'white',
                transform: 'scale(1.05)',
                '& .MuiTypography-root': {
                  color: 'white',
                },
              },
            }}
          >
            <Icon
              name="merge"
              type="fas"
              rotation={180}
              style={{
                fontSize: '15px',
                transform: 'rotate(-90deg)',
                mr: 0.3,
              }}
            />
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1,
                fontWeight: 500,
              }}
            >
              {prevGroup || ''}
            </Typography>
          </IconButton>
        )}

        <Chip
          label={formatMessage(messages.page, { num: pageNum })}
          size="small"
          color={isThisPageCurrent ? 'primary' : 'default'}
          variant={isThisPageCurrent ? 'filled' : 'outlined'}
          onClick={handlePageClick}
          clickable
          sx={{
            flex: 1,
            height: 28,
            borderRadius: '6px',
            fontWeight: isThisPageCurrent ? 600 : 500,
            opacity: 1,
            transform: 'scale(1)',
            '& .MuiChip-label': {
              px: 1.5,
              fontSize: '0.85rem',
              color: isThisPageCurrent ? 'white' : 'text.primary',
            },
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: isThisPageCurrent ? 'primary.dark' : 'primary.50',
              borderColor: 'primary.main',
              transform: 'translateY(-1px)',
              '& .MuiChip-label': {
                color: isThisPageCurrent ? 'white' : 'primary.main',
              },
            },
          }}
        />

        {/* Merge with next group button */}
        {isLastPage && canSplitNext && (
          <IconButton
            onClick={() => onMergeNext(groupIndex)}
            size="small"
            title={formatMessage(messages.mergeWithNextGroup)}
            sx={{
              bgcolor: 'white',
              color: (theme) => theme.palette.success.main,
              border: '1.5px solid',
              borderColor: (theme) => theme.palette.success.main,
              borderRadius: '6px',
              minWidth: '36px',
              height: '28px',
              fontSize: '0.75rem',
              padding: '4px 8px',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: (theme) => theme.palette.success.main,
                borderColor: (theme) => theme.palette.success.main,
                color: 'white',
                transform: 'scale(1.05)',
                '& .MuiTypography-root': {
                  color: 'white',
                },
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.75rem',
                lineHeight: 1,
                mr: 0.3,
                fontWeight: 500,
              }}
            >
              {nextGroup || ''}
            </Typography>
            <Icon
              name="merge"
              type="fas"
              rotation={0}
              style={{
                fontSize: '15px',
              }}
            />
          </IconButton>
        )}
      </Box>

      {/* Split zone between pages - expands on hover */}
      {canSplitNextPage && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 0,
            overflow: 'visible',
            my: 0,
            py: 0,
            transition: 'all 0.25s ease-out',
            '&:hover': {
              my: 1.5,
              py: 0.5,
            },
            '&:hover .split-button': {
              opacity: 1,
              transform: 'translate(-50%, -50%) scale(1)',
            },
            position: 'relative',
          }}
        >
          <IconButton
            className="split-button"
            onClick={() => onSplitPage(group[pageIndexInGroup + 1])}
            size="small"
            title={formatMessage(messages.splitHere)}
            sx={{
              bgcolor: (theme) => theme.palette.error.main,
              color: 'white',
              border: '1.5px solid',
              borderColor: (theme) => theme.palette.error.main,
              borderRadius: '6px',
              width: 28,
              height: 28,
              minWidth: 28,
              opacity: 0,
              transition: 'all 0.25s ease-out',
              position: 'absolute',
              top: '90%',
              left: '50%',
              transform: 'translate(-50%, -50%) scale(0.7)',
              '&:hover': {
                bgcolor: (theme) => theme.palette.error.dark,
                borderColor: (theme) => theme.palette.error.dark,
                transform: 'translate(-50%, -50%) scale(1.1)',
              },
            }}
          >
            <Icon
              name="split"
              type="fas"
              rotation={90}
              style={{ fontSize: 16 }}
            />
          </IconButton>
        </Box>
      )}
    </Box>
  );
}
